import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  UserPlus, 
  Users, 
  DollarSign, 
  Trash2, 
  CheckCircle,
  Clock,
  Search,
  Calendar
} from "lucide-react";

const API = '/api';

export default function EventRegistrations() {
  const { eventId } = useParams();
  const { getAuthHeader } = useAuth();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedShooter, setSelectedShooter] = useState("");
  const [registrationFee, setRegistrationFee] = useState("");
  const [feePaid, setFeePaid] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, registrationsRes, shootersRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/registrations`, { headers: getAuthHeader() }),
        axios.get(`${API}/shooters`),
      ]);
      setEvent(eventRes.data);
      setRegistrations(registrationsRes.data || []);
      setShooters(shootersRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load registration data");
    } finally {
      setLoading(false);
    }
  }, [eventId, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddRegistration = async () => {
    if (!selectedShooter) {
      toast.error("Please select a shooter");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/registrations`,
        {
          shooter_id: selectedShooter,
          registration_fee: parseFloat(registrationFee) || 0,
          fee_paid: feePaid,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Shooter registered successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register shooter");
    }
  };

  const handleTogglePayment = async (registration) => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/registrations/${registration.shooter_id}`,
        { fee_paid: !registration.fee_paid },
        { headers: getAuthHeader() }
      );
      toast.success(registration.fee_paid ? "Marked as unpaid" : "Marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const handleRemoveRegistration = async (shooterId) => {
    if (!window.confirm("Remove this registration?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/registrations/${shooterId}`,
        { headers: getAuthHeader() }
      );
      toast.success("Registration removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove registration");
    }
  };

  const resetForm = () => {
    setSelectedShooter("");
    setRegistrationFee("");
    setFeePaid(false);
    setSearchTerm("");
  };

  // Filter shooters not already registered
  const availableShooters = shooters.filter(
    (s) => !registrations.some((r) => r.shooter_id === s.id)
  );

  // Filter by search term
  const filteredShooters = availableShooters.filter(
    (s) =>
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.shooter_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalFees = registrations.reduce((sum, r) => sum + (parseFloat(r.registration_fee) || 0), 0);
  const collectedFees = registrations
    .filter((r) => r.fee_paid)
    .reduce((sum, r) => sum + (parseFloat(r.registration_fee) || 0), 0);
  const outstandingFees = totalFees - collectedFees;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="event-registrations-page" className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/events" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
          <ArrowLeft size={14} /> Back to Events
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
              <Users className="text-[#1B3B6F]" />
              Event Registration
            </h1>
            <p className="text-slate-500 mt-1">
              {event?.name} • {event?.start_date} - {event?.end_date}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="add-registration-btn"
              >
                <UserPlus size={20} className="mr-2" />
                Register Shooter
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Register Shooter</DialogTitle>
                <DialogDescription>
                  Add a shooter to this event
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Search */}
                <div>
                  <Label>Search Shooter</Label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                {/* Shooter Select */}
                <div>
                  <Label>Select Shooter</Label>
                  <Select value={selectedShooter} onValueChange={setSelectedShooter}>
                    <SelectTrigger data-testid="shooter-select">
                      <SelectValue placeholder="Choose a shooter..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredShooters.length === 0 ? (
                        <div className="p-2 text-sm text-slate-500">
                          {availableShooters.length === 0 
                            ? "All shooters already registered" 
                            : "No shooters match your search"}
                        </div>
                      ) : (
                        filteredShooters.map((shooter) => (
                          <SelectItem key={shooter.id} value={shooter.id}>
                            {shooter.name} {shooter.shooter_id && `(#${shooter.shooter_id})`}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Registration Fee */}
                <div>
                  <Label htmlFor="reg-fee">Registration Fee ($)</Label>
                  <Input
                    id="reg-fee"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={registrationFee}
                    onChange={(e) => setRegistrationFee(e.target.value)}
                    data-testid="registration-fee-input"
                  />
                </div>

                {/* Fee Paid Checkbox */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="fee-paid"
                    checked={feePaid}
                    onCheckedChange={setFeePaid}
                    data-testid="fee-paid-checkbox"
                  />
                  <Label htmlFor="fee-paid" className="cursor-pointer">
                    Mark registration fee as paid
                  </Label>
                </div>

                <Button 
                  onClick={handleAddRegistration}
                  className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  disabled={!selectedShooter}
                >
                  <UserPlus size={16} className="mr-2" />
                  Register Shooter
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Registered</p>
                <p className="text-2xl font-bold text-slate-800">{registrations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Collected</p>
                <p className="text-2xl font-bold text-green-600">${collectedFees.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock size={20} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold text-yellow-600">${outstandingFees.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <DollarSign size={20} className="text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Fees</p>
                <p className="text-2xl font-bold text-slate-800">${totalFees.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Registrations List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Calendar className="text-[#1B3B6F]" />
            Registered Shooters ({registrations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {registrations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">No registrations yet. Add your first shooter!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">#</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Shooter</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">ID</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Fee</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-600">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, idx) => (
                    <tr key={reg.shooter_id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-500">{idx + 1}</td>
                      <td className="py-3 px-4 font-medium">{reg.shooter_name}</td>
                      <td className="py-3 px-4 text-slate-500 font-mono">{reg.shooter_id_display || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        ${(parseFloat(reg.registration_fee) || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge 
                          className={reg.fee_paid 
                            ? "bg-green-100 text-green-800 cursor-pointer" 
                            : "bg-yellow-100 text-yellow-800 cursor-pointer"
                          }
                          onClick={() => handleTogglePayment(reg)}
                        >
                          {reg.fee_paid ? (
                            <><CheckCircle size={12} className="mr-1" /> Paid</>
                          ) : (
                            <><Clock size={12} className="mr-1" /> Unpaid</>
                          )}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleRemoveRegistration(reg.shooter_id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
